/** Lets WorldMap clear arrows / pending rows when `mvm:attack-cancelled` fires (wallet socket), even if `world:attack:cancelled` is missed. */
export const MVM_MAP_CLEAR_EVENT = 'eclipsera:mvm-map-clear'

/** Draw attacker return-home march when HTTP delivers outcome before/alongside a missed `mvm:attacker-return-march` socket. */
export const MVM_MAP_RETURN_MARCH_EVENT = 'eclipsera:mvm-map-return-march'

export type MvmMapClearDetail = {
  attackId: string
  toQ: number
  toR: number
}

export type MvmMapReturnMarchDetail = {
  attackId: string
  attackerWallet?: string
  defenderWallet?: string
  returnArrivesAt: string
  marchMs?: number
  fromQ: number
  fromR: number
  toQ: number
  toR: number
  marchWaypointHexes?: Array<{ q: number; r: number }>
}

export function dispatchMvmMapClear(detail: MvmMapClearDetail) {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(MVM_MAP_CLEAR_EVENT, { detail }))
}

export function dispatchMvmMapReturnMarch(detail: MvmMapReturnMarchDetail) {
  if (typeof window === 'undefined') return
  if (!detail?.attackId || !detail.returnArrivesAt) return
  window.dispatchEvent(new CustomEvent(MVM_MAP_RETURN_MARCH_EVENT, { detail }))
}

function numCoord(v: unknown): number | null {
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : null
}

/** Parse `returnMarch` from castle auto-resolve HTTP (`attack-arrived` / `confirm-battle`). */
export function parseReturnMarchFromApi(
  result: Record<string, unknown> | null | undefined,
): MvmMapReturnMarchDetail | null {
  if (!result) return null
  const raw = result.returnMarch
  if (!raw || typeof raw !== 'object') return null
  const rm = raw as Record<string, unknown>
  const attackId = rm.attackId != null ? String(rm.attackId).trim() : ''
  const returnArrivesAt = rm.returnArrivesAt != null ? String(rm.returnArrivesAt).trim() : ''
  const fromQ = numCoord(rm.fromQ)
  const fromR = numCoord(rm.fromR)
  const toQ = numCoord(rm.toQ)
  const toR = numCoord(rm.toR)
  if (!attackId || !returnArrivesAt || fromQ == null || fromR == null || toQ == null || toR == null) {
    return null
  }
  const retEnd = new Date(returnArrivesAt).getTime()
  if (!Number.isFinite(retEnd) || retEnd <= Date.now()) return null
  const marchMsRaw = rm.marchMs != null ? Number(rm.marchMs) : NaN
  const marchMs = Number.isFinite(marchMsRaw) && marchMsRaw > 0 ? marchMsRaw : Math.max(1, retEnd - Date.now())
  const wpRaw = rm.marchWaypointHexes
  const marchWaypointHexes = Array.isArray(wpRaw)
    ? wpRaw
        .map((h) => {
          if (!h || typeof h !== 'object') return null
          const o = h as { q?: unknown; r?: unknown }
          const q = numCoord(o.q)
          const r = numCoord(o.r)
          return q != null && r != null ? { q, r } : null
        })
        .filter((x): x is { q: number; r: number } => x != null)
    : undefined
  return {
    attackId,
    attackerWallet: rm.attackerWallet != null ? String(rm.attackerWallet) : undefined,
    defenderWallet: rm.defenderWallet != null ? String(rm.defenderWallet) : undefined,
    returnArrivesAt,
    marchMs,
    fromQ,
    fromR,
    toQ,
    toR,
    ...(marchWaypointHexes?.length ? { marchWaypointHexes } : {}),
  }
}

export function dispatchReturnMarchFromApi(result: Record<string, unknown> | null | undefined) {
  const parsed = parseReturnMarchFromApi(result)
  if (parsed) dispatchMvmMapReturnMarch(parsed)
}
